import { compact, partition } from "lodash";
import {
  GetOrderResponse,
  PortalNotification, PortalNotificationReference, PortalNotificationType,
} from '../types'
import { isAdhocPortalOrder } from "./isAdhocPortalOrder";

function sortByDate(a: string, b: string) {
  return (Date.parse(b) || 0) - (Date.parse(a) || 0)
}

function sortByName(a: string, b: string) {
  return (a).localeCompare(b);
}

export function checkIfPastDue(dueDate: string) {
  return new Date(dueDate) < new Date()
}

export function isMessageNotificationType(type: PortalNotificationType) {
  return type === 'Message'
}

export function isTaskNotificationType(type: PortalNotificationType) {
  return type !== 'Message'
}

export function hasUnreadReplies(notification: PortalNotification | PortalNotificationReference) {
  // readStatus reflects whether the recipient of that reply has read it. Only replies from
  // the title company (not the portal user's own sent replies) count toward the portal user's unread state.
  return (notification.Replies ?? []).some(reply => reply.Source === 'liveLook' && reply.AdditionalData?.readStatus === false)
}

export function getMostRecentActivityDate(notification: PortalNotificationReference) {
  const dates = [notification.CreatedDateTime, ...(notification.Replies ?? []).map(reply => reply.CreatedDate)]
  return dates.reduce((latest, date) => (Date.parse(date) > Date.parse(latest) ? date : latest))
}

export function getMostRecentContent(notification: PortalNotificationReference): string {
  const replies = notification.Replies ?? []
  if (replies.length === 0) return notification.Content ?? ''

  const mostRecentReply = replies.reduce((latest, reply) =>
    Date.parse(reply.CreatedDate) > Date.parse(latest.CreatedDate) ? reply : latest
  )

  if (Date.parse(mostRecentReply.CreatedDate) <= Date.parse(notification.CreatedDateTime)) {
    return notification.Content ?? ''
  }

  return typeof mostRecentReply.AdditionalData?.message === 'string' ? mostRecentReply.AdditionalData.message : ''
}

/**
 * Sort the given notifications and return a data structure grouping them into
 * the various types, with description text for the ui.
 */
export function getNotificationsInfo(_notifications: PortalNotificationReference[], uiContent: any, orderData: GetOrderResponse) {
  const { TaskText, TasksText, MessageText, MessagesText } = uiContent

  const notifications = compact(_notifications)

  const sortedNotifications: PortalNotificationReference[] = notifications.sort((a, b) => {
    const dateSort = sortByDate(getMostRecentActivityDate(a), getMostRecentActivityDate(b))
    if (dateSort) return dateSort
    return sortByName(a.NotificationTitle, b.NotificationTitle)
  })

  // Tasks are any non-Message notification.
  const tasks = sortedNotifications.filter(n => isTaskNotificationType(n.Type))
  const wiringInstructionsTaskRef = sortedNotifications.find(n => n.Type === 'WiringInstructionsTask')
  const messages = sortedNotifications.filter(n => isMessageNotificationType(n.Type))

  let [unreadMessages, readMessages] = partition(messages, message => !message.ReadReceipt || hasUnreadReplies(message))
  const pastDue = !isAdhocPortalOrder(orderData.Order) && orderData && orderData.Order && orderData.Order?.CashToCloseDetails && checkIfPastDue(orderData.Order.CashToCloseDetails.dueDate)

  //CashToCloseTask that is past due will be marked as a complete task here.
  const [incompleteTasks, completeTasks] = partition(
    tasks,
    task =>
      task.IsComplete === false && !(task.Type === 'CashToCloseTask' && pastDue)
  )

  const unreadMessagesCount = unreadMessages.length
  const incompleteTasksCount = incompleteTasks.length
  const messagesCountText = unreadMessagesCount ? `You have ${unreadMessages.length} new ${unreadMessages.length > 1 ? MessagesText : MessageText}!` : ''
  const tasksCountText = incompleteTasksCount ? `You have ${incompleteTasks.length} ${incompleteTasks.length > 1 ? TasksText : TaskText} to complete!` : ''

  const hasWiringInstructionsTask = !!wiringInstructionsTaskRef
  const wiringInstructionsTaskIsIncomplete = hasWiringInstructionsTask && !wiringInstructionsTaskRef.IsComplete
  const wiringInstructionsTaskText = wiringInstructionsTaskIsIncomplete
    ? "New instructions available!"
    : ""
  ;

  return {
    notifications,
    hasMessages: messages.length > 0,
    messagesCountText,
    messages,
    unreadMessages,
    readMessages,

    hasTasks: tasks.length > 0,
    tasksCountText,
    tasks,
    incompleteTasks,
    completeTasks,

    hasWiringInstructionsTask,
    wiringInstructionsTaskRef,
    wiringInstructionsTaskText,
  };
}


export type NotificationsInfo = ReturnType<typeof getNotificationsInfo>
